import { Check, ChevronDown, ChevronRight, SearchIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { EVENTS } from "~/containers/os-agents/constants"
import useGlobalStore from "~/store/global"
import useOSAgentsIdStore from "~/store/os.agents_.$id"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"

const ThoughtTimer = ({
  isPending,
  messageId
}: {
  isPending: boolean
  messageId: string
}) => {
  const { t } = useTranslation()
  const thoughtTimerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState("completed")
  const [currentStep, setCurrentStep] = useState(0)
  const setElapsedTime = useOSAgentsIdStore((store) => store.setElapsedTime)
  const elapsedTime = useOSAgentsIdStore(
    (store) => store.elapsedTimes[messageId]
  )
  const [isExpanded, setIsExpanded] = useState(false)
  const tenantName = useGlobalStore((store) => store.tenant?.name)
  const steps = [
    {
      id: 1,
      title: t("k_os.label_searching_relevant_content"),
      actionText: [
        t("k_os.label_sifting_tenant_insights", {
          tenantName: tenantName || "OrbitShift"
        }),
        t("k_os.label_looking_for_additional_insights")
      ],
      duration: 1000
    },
    {
      id: 2,
      title: t("k_os.label_generating_response"),
      duration: 1500
    }
  ]

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (status === "thinking") {
      interval = setInterval(() => {
        setElapsedTime(messageId, 0.1)
      }, 100)
    }
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, messageId])

  useEffect(() => {
    if (currentStep < steps.length - 1) {
      const timeout = setTimeout(() => {
        setCurrentStep((prev) => prev + 1)
        thoughtTimerRef.current?.scrollIntoView()
      }, steps[currentStep].duration)
      return () => clearTimeout(timeout)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  useEffect(() => {
    if (!isPending && currentStep === steps.length - 1) {
      setStatus("completed")
    } else if (isPending) {
      setStatus("thinking")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending, currentStep])

  const progressPercentage = Math.min(
    (currentStep / steps.length) * 100 + (status === "thinking" ? 10 : 0),
    100
  )

  if (status === "completed") {
    return (
      <div className="max-w-2xl w-full dark:text-zinc-300 text-zinc-600">
        <div
          className="flex items-center gap-3 cursor-pointer transition-colors"
          onClick={() => {
            track(EVENTS.THOUGHT_TIMER_TOGGLED, {
              ...getBaseEventProps({ feature: "os_agents" }),
              is_expanded: String(!isExpanded)
            })
            setIsExpanded(!isExpanded)
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-controls="thought-timer-content"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setIsExpanded(!isExpanded)
            }
          }}
        >
          <div className="border border-primary dark:bg-dark-2 p-1 rounded-full flex-shrink-0">
            <img src="/favicon.png" alt="" className="w-5 h-5" />
          </div>
          <span className="dark:text-zinc-300 text-zinc-500 font-medium text-13">
            {t("os_agents.label_thought_for_seconds", {
              seconds: Math.floor(elapsedTime) || 1
            })}
          </span>
          <div className="text-zinc-600 dark:text-zinc-300">
            {isExpanded ? (
              <ChevronDown size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="relative select-none mt-4 ml-10">
            <div className="absolute left-[11px] top-4 bottom-4 w-[1px] bg-genesis dark:bg-zinc-800 z-0" />
            {steps.map((step) => {
              return (
                <div
                  key={step.id}
                  className={`relative flex gap-4 pb-3 last:pb-0 z-10 transition-all duration-500`}
                >
                  <div
                    className={`flex items-center justify-center w-6 h-6 rounded-full border flex-shrink-0 bg-white dark:bg-[#121212] dark:border-zinc-700 border-light-border text-emerald-500`}
                  >
                    <Check size={14} strokeWidth={3} />
                  </div>
                  <div className="flex-1 flex flex-col gap-2 pt-1">
                    <p className="text-13 text-zinc-500 dark:text-zinc-500">
                      {step.title}
                    </p>

                    {step.actionText && step.actionText?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {step.actionText?.map((actionText) => (
                          <div
                            key={actionText}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-[11px] border-gray-300 dark:border-border text-zinc-500 dark:text-zinc-500`}
                          >
                            <SearchIcon size={12} />
                            {actionText}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-2xl w-full text-zinc-400">
      <div className="flex items-start gap-4">
        <div className="border border-primary dark:bg-dark-2 p-1 rounded-full animate-pulse flex-shrink-0">
          <img src="/favicon.png" alt="" className="w-5 h-5" />
        </div>
        <div className="flex flex-col gap-3 flex-1">
          <div className="flex items-center justify-between gap-3">
            <span className="dark:text-zinc-300 text-zinc-500 font-medium text-sm">
              Step {Math.min(currentStep + 1, steps.length)} of {steps.length}
            </span>
            <span className="text-zinc-500 text-xs">1 min left</span>
          </div>
          <div className="h-[6px] w-full bg-genesis dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500 ease-out rounded-full animate-pulse"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      <div className="relative ml-10 mt-4">
        <div className="absolute left-3 top-4 bottom-4 w-[1px] bg-genesis dark:bg-zinc-800 z-0" />

        {steps.map((step, index) => {
          const isCompleted = index < currentStep
          const isActive = index === currentStep
          const isPending = index > currentStep

          return (
            <div
              key={step.id}
              className={`relative flex gap-4 pb-3 last:pb-0 z-10 transition-all duration-500 ${isActive ? "opacity-100" : isPending ? "opacity-30" : "opacity-60"}`}
            >
              <div
                className={`flex items-center justify-center w-6 h-6 rounded-full border-2 flex-shrink-0 bg-white dark:bg-[#121212] ${isCompleted ? "dark:border-zinc-700 border-light-border text-emerald-500" : isActive ? "dark:border-emerald-500/50 border-emerald-500/50 text-emerald-400 ring-4 ring-emerald-500/10 border-dashed animate-pulse" : "dark:border-light-border border-light-border text-zinc-600"}`}
              >
                {isCompleted ? (
                  <Check size={14} strokeWidth={3} />
                ) : isActive ? (
                  <span className="text-xs font-bold text-emerald-400">
                    {index + 1}
                  </span>
                ) : (
                  <span className="text-xs font-bold">{index + 1}</span>
                )}
              </div>
              <div className="flex-1 flex flex-col gap-2 pt-1">
                <p className="text-13 text-zinc-500 dark:text-zinc-500">
                  {step.title}
                </p>
                {(isActive || isCompleted) &&
                  step.actionText &&
                  step.actionText?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {step.actionText?.map((actionText) => (
                        <div
                          key={actionText}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] border-gray-300 dark:border-border text-zinc-500 dark:text-zinc-500`}
                        >
                          <SearchIcon size={12} />
                          {actionText}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          )
        })}
      </div>
      <div ref={thoughtTimerRef} />
    </div>
  )
}

export default ThoughtTimer
